import { usePermission } from '../../../../auth/AuthContext';
import { Avatar, type AvatarSize } from '../../../../components/Avatar';
import { Tooltip } from '../../../../components/Tooltip';
import { usePersonPhoto } from '../../../human-resources/personnel/usePersonPhoto';

/**
 * One employee's small picture. The photograph is FETCHED through the authenticated client and
 * becomes an object URL for this tab (`usePersonPhoto`) — never an `<img src>` to the endpoint.
 * No photograph, no `personnel.read`, or a restricted member: initials, or a neutral disc.
 */
export function MemberAvatar({
  personId,
  name,
  size = 'sm',
  className = '',
}: {
  /** Absent above the reader's access level — and then nothing is fetched. */
  personId?: string | null;
  name: string;
  size?: AvatarSize;
  className?: string;
}) {
  const canReadPeople = usePermission('personnel.read');
  const photo = usePersonPhoto(canReadPeople && personId != null ? personId : null);

  return <Avatar name={name} imageUrl={photo.url} size={size} className={className} />;
}

/**
 * The Personnel cell of Survey › Development Plan — the employees a plan was created for, as a
 * row of small pictures, each naming its employee in a `<Tooltip>`. A plan for many shows the
 * first few and counts the rest in words, so the cell keeps one height.
 */
export function SurveyPlanMembers({
  members,
  nameOf,
  max = 5,
}: {
  members: { respondentId: string; personId?: string | null }[];
  nameOf: (member: { respondentId: string; personId?: string | null }) => string;
  max?: number;
}) {
  const shown = members.slice(0, max);
  const rest = members.length - shown.length;

  if (members.length === 0) return <span className="text-sm text-slate-400">—</span>;

  return (
    <span className="flex items-center gap-1.5">
      <span className="flex items-center">
        {shown.map((member, index) => (
          <Tooltip key={member.respondentId} label={nameOf(member)}>
            {/* Overlapped, each on a white edge so two pictures stay two. */}
            <span className={`inline-flex rounded-full border-2 border-white ${index > 0 ? '-ml-2' : ''}`}>
              <MemberAvatar personId={member.personId} name={nameOf(member)} />
              <span className="sr-only">{nameOf(member)}</span>
            </span>
          </Tooltip>
        ))}
      </span>

      {rest > 0 ? <span className="font-mono text-xs tabular-nums text-slate-500">+{rest} more</span> : null}
    </span>
  );
}
